<template>
  <section class="p-6 max-w-6xl mx-auto">
    <div class="flex items-end justify-between mb-5 gap-4 flex-wrap">
      <div>
        <h1 class="font-heading font-bold text-xl">New outbound campaign</h1>
        <p class="text-[13px] text-gray-500 dark:text-dark-text-muted mt-1">
          One-off or bulk messages outside standard flows — B2B outreach, peak
          period notices, etc.
        </p>
      </div>
      <div class="flex gap-2">
        <button class="btn btn-outline btn-sm">Load saved campaign</button>
        <button class="btn btn-publish" @click="modal = 'send'">
          Review &amp; Send →
        </button>
      </div>
    </div>

    <div class="card card-body mb-6 flex gap-3 items-start text-[13px]">
      <info-icon class="w-4 h-4 text-brand-teal flex-shrink-0 mt-0.5" />
      <div>
        Bulk sends bypass standard flow triggers. Only send to recipients with
        confirmed CASL consent. Publisher approval required before messages go
        out.
      </div>
    </div>

    <div class="grid md:grid-cols-2 gap-4 mb-4">
      <div class="space-y-4">
        <!-- 1 recipients -->
        <div class="card">
          <div class="card-header">
            <div class="card-title">1 · Define recipients</div>
          </div>
          <div class="card-body space-y-4">
            <div>
              <label class="field-label">Recipient source</label>
              <select class="field">
                <option selected>Filter from Shopify orders</option>
                <option>Upload CSV</option>
                <option>Manual entry</option>
                <option>Saved segment</option>
              </select>
            </div>
            <div class="grid grid-cols-2 gap-4">
              <div>
                <label class="field-label">Delivery date</label>
                <input type="date" class="field" value="2025-02-14" />
              </div>
              <div>
                <label class="field-label">BU filter</label>
                <select class="field">
                  <option selected>Callia Core</option>
                  <option>Lakewood</option>
                  <option>Sweetheart Rose</option>
                  <option>All BUs</option>
                </select>
              </div>
            </div>
            <div
              class="flex items-center gap-3 px-3 py-3 rounded-lg bg-brand-light-blue bg-opacity-50 dark:bg-brand-teal dark:bg-opacity-10"
            >
              <div
                class="text-2xl font-bold font-mono text-brand-teal-dark dark:text-brand-light-blue"
              >
                2,847
              </div>
              <div>
                <div
                  class="text-[13px] font-semibold text-brand-teal-dark dark:text-brand-light-blue"
                >
                  Recipients matched
                </div>
                <div
                  class="text-[12px] text-gray-500 dark:text-dark-text-muted mt-0.5"
                >
                  Feb 14, 2025 · Callia Core · Consented only
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- 2 template -->
        <div class="card">
          <div class="card-header">
            <div class="card-title">2 · Select template</div>
          </div>
          <div class="card-body space-y-2">
            <button
              v-for="(tpl, i) in bulkTemplates"
              :key="tpl.name"
              class="w-full text-left px-3.5 py-2.5 rounded-lg border transition-colors"
              :class="
                selectedTemplate === i
                  ? 'border-brand-teal bg-brand-light-blue bg-opacity-40 dark:bg-brand-teal dark:bg-opacity-15'
                  : 'border-gray-200 dark:border-dark-border hover:bg-gray-50 dark:hover:bg-dark-card-hover'
              "
              @click="selectTemplate(i)"
            >
              <div class="text-[13px] font-semibold">{{ tpl.name }}</div>
              <div
                class="text-[12px] text-gray-500 dark:text-dark-text-muted mt-0.5"
              >
                {{ tpl.preview }}
              </div>
            </button>
          </div>
        </div>
      </div>

      <div class="space-y-4">
        <!-- 3 compose -->
        <div class="card">
          <div class="card-header">
            <div class="card-title">3 · Compose message</div>
          </div>
          <div class="card-body">
            <label class="field-label">Message</label>
            <textarea
              ref="bulkTextarea"
              v-model="bulkMsg"
              class="field min-h-[130px] font-mono text-[13px]"
              aria-label="Bulk message"
            ></textarea>

            <div class="mt-3.5">
              <div class="field-label mb-2">Insert variable</div>
              <div class="flex flex-wrap gap-1.5">
                <button
                  v-for="v in bulkVars"
                  :key="v"
                  class="btn btn-outline btn-xs font-mono"
                  @click="insertBulkVar(v)"
                >
                  {{ v }}
                </button>
              </div>
            </div>

            <div
              class="flex items-center gap-2.5 mt-3 text-[12px] text-gray-500 dark:text-dark-text-muted"
            >
              <span
                >Character count: {{ bulkSeg.chars }} /
                {{ bulkSeg.limit }}</span
              >
              <span
                class="badge"
                :class="bulkSeg.segments >= 2 ? 'badge-draft' : 'badge-live'"
                >{{ bulkSeg.segments }} SMS segment{{
                  bulkSeg.segments > 1 ? "s" : ""
                }}</span
              >
            </div>
          </div>
        </div>

        <!-- 4 schedule -->
        <div class="card">
          <div class="card-header">
            <div class="card-title">4 · Schedule &amp; send</div>
          </div>
          <div class="card-body space-y-4">
            <div>
              <label class="field-label">Send time</label>
              <select class="field">
                <option selected>Scheduled</option>
                <option>Send immediately after approval</option>
              </select>
            </div>
            <div class="grid grid-cols-2 gap-4">
              <div>
                <label class="field-label">Date</label>
                <input type="date" class="field" value="2025-02-13" />
              </div>
              <div>
                <label class="field-label">Time (local)</label>
                <input type="time" class="field" value="10:00" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- preview -->
    <div class="card">
      <div class="card-header">
        <div class="card-title">Message preview</div>
        <span class="badge badge-teal">Sample: first recipient</span>
      </div>
      <div class="card-body flex gap-4">
        <div class="w-20 flex-shrink-0 text-right text-[12px] font-medium">
          Feb 13<br /><span
            class="text-[10px] text-gray-400 dark:text-dark-text-muted"
            >10:00 AM</span
          >
        </div>
        <div>
          <div class="sms-bubble">{{ bulkPreview }}</div>
          <div
            class="flex flex-wrap gap-x-2 mt-1.5 text-[11px] text-gray-500 dark:text-dark-text-muted"
          >
            <span
              >{{ bulkSeg.segments }} SMS segment{{
                bulkSeg.segments > 1 ? "s" : ""
              }}
              · {{ bulkSeg.chars }} chars</span
            >
            <span>· 2,847 recipients</span>
            <span>· Estimated cost: ~$340 CAD</span>
          </div>
        </div>
      </div>
    </div>

    <!-- Confirm bulk send modal -->
    <div
      v-if="modal === 'send'"
      class="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-40"
      @click.self="modal = null"
    >
      <div
        class="card w-full max-w-md p-6"
        role="dialog"
        aria-modal="true"
        aria-labelledby="send-modal-title"
      >
        <div
          id="send-modal-title"
          class="font-heading font-bold text-base mb-2"
        >
          Confirm bulk send
        </div>
        <p class="text-[13px] text-gray-500 dark:text-dark-text-muted mb-4">
          You're about to schedule 2,847 messages for February 13 at 10:00 AM.
          This action will be logged and requires Publisher-level access.
        </p>
        <div
          class="rounded-lg bg-gray-50 dark:bg-dark-surface p-3 mb-4 grid grid-cols-2 gap-3"
        >
          <div>
            <div
              class="text-[11px] text-gray-500 dark:text-dark-text-muted uppercase tracking-wide"
            >
              Recipients
            </div>
            <div class="text-xl font-semibold font-mono mt-1">2,847</div>
          </div>
          <div>
            <div
              class="text-[11px] text-gray-500 dark:text-dark-text-muted uppercase tracking-wide"
            >
              Scheduled for
            </div>
            <div class="text-sm font-semibold font-mono mt-1">
              Feb 13 · 10:00 AM
            </div>
          </div>
          <div>
            <div
              class="text-[11px] text-gray-500 dark:text-dark-text-muted uppercase tracking-wide"
            >
              Template
            </div>
            <div class="text-[13px] font-medium mt-1">
              {{ bulkTemplates[selectedTemplate].name }}
            </div>
          </div>
          <div>
            <div
              class="text-[11px] text-gray-500 dark:text-dark-text-muted uppercase tracking-wide"
            >
              Est. cost
            </div>
            <div class="text-sm font-semibold font-mono mt-1">~$340 CAD</div>
          </div>
        </div>
        <div
          class="rounded-lg bg-amber-50 dark:bg-amber-900 dark:bg-opacity-20 border border-amber-300 dark:border-amber-700 dark:border-opacity-40 px-3 py-2.5 mb-5 text-[12.5px] text-amber-800 dark:text-amber-300"
        >
          ⚠️ This cannot be undone once scheduled. Double-check recipient count
          and send time.
        </div>
        <div class="flex justify-end gap-2">
          <button class="btn btn-outline" @click="modal = null">Cancel</button>
          <button class="btn btn-publish" @click="modal = null">
            Confirm &amp; schedule
          </button>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup>
import { ref } from "vue";
import { InfoIcon } from "vue-feather-icons";
import { useSmsBulk } from "./composables/useSmsBulk";

const modal = ref(null);

const {
  bulkMsg,
  bulkTextarea,
  selectedTemplate,
  bulkVars,
  bulkTemplates,
  selectTemplate,
  bulkSeg,
  bulkPreview,
  insertBulkVar
} = useSmsBulk();
</script>
